// PRD.324 — Rich Text as a FLOW ANCHOR in Render mode.
//
// THE PROBLEM. Every other mapping is a fixed rectangle: x/y/w/h as percentages
// of its template page. Rich Text cannot be, because its height depends on
// patient-specific content that does not exist when the admin places the box.
// Clipping it, stretching it, or stacking it with z-index would all lose
// content — so the box is treated as a START POSITION and a WIDTH, and the
// height comes from the content at render time.
//
// WHY A PURE ENGINE. Measuring text needs a DOM; deciding what lands on which
// page does not. Splitting them means the pagination rules — the part that is
// easy to get subtly wrong — are unit-tested, while the DOM layer only answers
// "how tall is this block?".
//
//   measure (DOM)  ──block heights──►  paginate (here)  ──►  render
//
// UNITS. Everything is a percentage of ONE page's height, matching the existing
// mapping coordinate system exactly, so no new unit or conversion is
// introduced.
//
// Import-free so it runs under the node:test harness.

/**
 * Canonical A4 page geometry for the Render surface.
 *
 * THE PAGE IS A SHEET, NOT A CONTAINER. Before this, a rendered page was
 * whatever height the uploaded template image happened to be, and flowed
 * content was an absolutely-positioned overlay with no bound — so text simply
 * painted past the image's bottom edge instead of breaking. A page now has a
 * FIXED height derived from this ratio, which is what makes "does this fit?"
 * a real question with a real answer.
 *
 * One constant, used by measurement, pagination and rendering alike, so a
 * scale/zoom mismatch cannot make the algorithm believe content fits when it
 * does not: every calculation is a percentage of the same sheet.
 */
export const A4_WIDTH_MM = 210;
export const A4_HEIGHT_MM = 297;
/** Height of one sheet as a multiple of its rendered width. */
export const A4_ASPECT = A4_HEIGHT_MM / A4_WIDTH_MM;

/** Page height in CSS pixels for a sheet rendered at `widthPx`. */
export function a4PageHeightPx(widthPx: number): number {
  return widthPx * A4_ASPECT;
}

/** A measured Rich Text block: one paragraph or one list item. */
export interface FlowBlock {
  /** Index into the document's `blocks`, so the renderer can draw the original. */
  index: number;
  /** Rendered height as a percentage of one page's height. */
  heightPct: number;
  /**
   * Height of each RENDERED LINE within this block, measured in the browser.
   *
   * Only needed when a single block is taller than a page and must therefore be
   * split internally. Absent for the common case, where a block moves whole.
   *
   * Line heights come from real layout (client rects), never from a
   * characters-per-line estimate — wrapping depends on the font, the marks and
   * the box width, none of which a character count knows about.
   */
  lineHeightsPct?: number[];
}

/**
 * A piece of a block placed on one page.
 *
 * `fromLine`/`toLine` are absent when the whole block is placed, which is the
 * normal case; they appear only for a block that had to be split internally.
 */
export interface FlowPiece {
  blockIndex: number;
  /** Inclusive first rendered line, when this is a partial placement. */
  fromLine?: number;
  /** EXCLUSIVE last rendered line, when this is a partial placement. */
  toLine?: number;
}

/** A mapping that participates in layout. Only geometry matters here. */
export interface FlowMapping {
  id: string;
  y_pct: number;
  h_pct: number;
  /**
   * PRD.325 — a FIXED mapping that the flow must never move.
   *
   * The Form Header is template chrome bound to its page, not content that
   * follows the patient's text. Without this, a header placed below the anchor
   * would satisfy the geometric downstream rule and get pushed onto a
   * continuation page, which is exactly the repetition PRD.325 forbids.
   */
  fixed?: boolean;
}

/** One page's worth of the Rich Text flow. */
export interface FlowFragment {
  /** 0-based index into the RESULT page list, not the template page list. */
  pageIndex: number;
  /** Where this fragment starts, as a percentage of page height. */
  startYPct: number;
  /**
   * Block indices drawn on this page, in order.
   *
   * Retained as the simple view for callers that only need "which blocks
   * appear here"; `pieces` carries the line range when a block was split.
   */
  blockIndices: number[];
  /** Exact placements, including partial ones. */
  pieces: FlowPiece[];
  /** Height consumed, so downstream placement can start below it. */
  heightPct: number;
}

/** Where a downstream mapping ends up after the flow pushed it. */
export interface DownstreamPlacement {
  mappingId: string;
  pageIndex: number;
  /** Translated y. `x`/`w`/`h` are never changed. */
  yPct: number;
}

export interface FlowLayout {
  fragments: FlowFragment[];
  downstream: DownstreamPlacement[];
  /**
   * Continuation pages required BEYOND the anchor's own page, i.e. blank A4
   * pages the renderer generates. Never persisted.
   */
  continuationPages: number;
}

/**
 * Bottom margin left on every page, as a percentage of page height.
 *
 * Content is not run to the physical edge: printers cannot reach it, and a
 * signature flush against the paper edge looks like a rendering fault.
 */
export const PAGE_BOTTOM_MARGIN_PCT = 4;

/** Where flowed content resumes on a generated continuation page. */
export const CONTINUATION_TOP_PCT = 6;

/**
 * Classify which mappings a flow anchor pushes.
 *
 * THE RULE IS PURELY GEOMETRIC — a mapping on the anchor's page whose top edge
 * is at or below the anchor's top edge is downstream. Deliberately NOT based on
 * field labels, names or types, which the PRD forbids and which would break the
 * moment a clinic renamed a field.
 *
 * `>=` rather than `>` so a control placed level with the anchor still moves:
 * anything not strictly above it would otherwise be overlapped by the expanding
 * text, and overlap is exactly what this feature exists to prevent.
 *
 * Ordering is by `y` then `x`, so side-by-side controls (a signature beside its
 * date) keep their reading order and their relative offsets below.
 */
export function classifyDownstream(
  anchor: FlowMapping,
  samePageMappings: readonly FlowMapping[],
): FlowMapping[] {
  return samePageMappings
    .filter((m) => m.id !== anchor.id && !m.fixed && m.y_pct >= anchor.y_pct)
    .slice()
    .sort((a, b) => a.y_pct - b.y_pct || a.id.localeCompare(b.id));
}

/**
 * Group downstream mappings into ATOMIC ROWS.
 *
 * Controls that overlap vertically are one row and move together, which is what
 * preserves a "Signature | Date" pair sitting side by side. A row is never
 * split across a page boundary — a signature box pushed half onto the next page
 * would be worse than moving the whole row.
 */
export function groupDownstreamRows(mappings: readonly FlowMapping[]): FlowMapping[][] {
  const rows: FlowMapping[][] = [];
  for (const m of mappings) {
    const row = rows[rows.length - 1];
    if (row) {
      // Vertical overlap with the row's current extent means "same row".
      const rowTop = Math.min(...row.map((r) => r.y_pct));
      const rowBottom = Math.max(...row.map((r) => r.y_pct + r.h_pct));
      if (m.y_pct < rowBottom && m.y_pct + m.h_pct > rowTop) {
        row.push(m);
        continue;
      }
    }
    rows.push([m]);
  }
  return rows;
}

/**
 * Lay out one Rich Text flow and everything it pushes.
 *
 * The anchor's authored HEIGHT is deliberately ignored for clipping: it is a
 * builder affordance, not a runtime limit. Its authored Y is honoured exactly,
 * so the admin's placement is respected.
 *
 * Blocks are never split internally — a paragraph or list item moves to the
 * next page whole. That is the "prefer natural block breaks" rule, and it also
 * means the renderer never has to reason about half a paragraph.
 */
export function layoutRichTextFlow(input: {
  anchor: FlowMapping;
  blocks: readonly FlowBlock[];
  /** Every other mapping on the anchor's template page. */
  samePageMappings: readonly FlowMapping[];
}): FlowLayout {
  const { anchor, blocks } = input;
  const downstreamMappings = classifyDownstream(anchor, input.samePageMappings);
  const rows = groupDownstreamRows(downstreamMappings);

  const limit = 100 - PAGE_BOTTOM_MARGIN_PCT;
  const fragments: FlowFragment[] = [];

  let pageIndex = 0;
  let cursor = anchor.y_pct;
  let current: FlowFragment = {
    pageIndex,
    startYPct: cursor,
    blockIndices: [],
    pieces: [],
    heightPct: 0,
  };

  const nextPage = () => {
    fragments.push(current);
    pageIndex += 1;
    cursor = CONTINUATION_TOP_PCT;
    current = {
      pageIndex,
      startYPct: cursor,
      blockIndices: [],
      pieces: [],
      heightPct: 0,
    };
  };

  const place = (piece: FlowPiece, heightPct: number) => {
    current.pieces.push(piece);
    if (!current.blockIndices.includes(piece.blockIndex)) {
      current.blockIndices.push(piece.blockIndex);
    }
    cursor += heightPct;
    current.heightPct = cursor - current.startYPct;
  };

  for (const block of blocks) {
    const isPageEmpty = current.pieces.length === 0;

    // ── 1–3. Break at a BLOCK boundary whenever that is enough ──
    if (cursor + block.heightPct <= limit) {
      place({ blockIndex: block.index }, block.heightPct);
      continue;
    }
    if (!isPageEmpty) {
      // Move the whole block to a fresh page and try again there.
      nextPage();
      if (cursor + block.heightPct <= limit) {
        place({ blockIndex: block.index }, block.heightPct);
        continue;
      }
    }

    // ── 4. The block does not fit even on an empty page: split by LINE ──
    //
    // Reached only when a single paragraph or list item is itself taller than a
    // page, which block-level breaking alone cannot solve. Line heights come
    // from real browser layout, so the split lands on a rendered line boundary
    // and the formatting on either side stays intact.
    const lines = block.lineHeightsPct;
    if (!lines || lines.length === 0) {
      // Unsplittable (never measured per line). Place it whole rather than
      // dropping it or looping — visible overflow beats silent data loss.
      place({ blockIndex: block.index }, block.heightPct);
      continue;
    }

    let from = 0;
    while (from < lines.length) {
      let used = 0;
      let to = from;
      while (to < lines.length && cursor + used + lines[to] <= limit) {
        used += lines[to];
        to += 1;
      }

      if (to === from) {
        // FORWARD-PROGRESS GUARD. Not one line fits in the space left.
        if (current.pieces.length > 0) {
          // There is content above; a fresh page gives the full budget.
          nextPage();
          continue;
        }
        // Already on an empty page and a single line still will not fit — the
        // line is taller than a whole page. Place it anyway: looping here would
        // hang the renderer, and dropping it would lose the patient's text.
        used = lines[from];
        to = from + 1;
      }

      place({ blockIndex: block.index, fromLine: from, toLine: to }, used);
      from = to;
      if (from < lines.length) nextPage();
    }
  }

  // The final fragment is kept even when empty, so an empty Rich Text still
  // reports a position for downstream placement instead of vanishing.
  fragments.push(current);

  // ─── Downstream reflow ───
  //
  // Rows are re-laid below the flow, preserving each row's internal spacing:
  // every member keeps its offset from the row's top edge, so a side-by-side
  // pair stays side by side and a deliberate gap between rows survives.
  const downstream: DownstreamPlacement[] = [];
  let flowBottom = cursor;
  let flowPage = pageIndex;

  for (const row of rows) {
    const rowTop = Math.min(...row.map((r) => r.y_pct));
    const rowBottom = Math.max(...row.map((r) => r.y_pct + r.h_pct));
    const rowHeight = rowBottom - rowTop;
    // Gap the admin left between this row and whatever precedes it, preserved
    // rather than collapsed.
    const gap = Math.max(0, rowTop - anchor.y_pct - (anchor.h_pct || 0));

    let top = flowBottom + (downstream.length === 0 ? gap : 1);
    if (top + rowHeight > limit) {
      // The row does not fit — move the WHOLE row, never clip it.
      flowPage += 1;
      top = CONTINUATION_TOP_PCT;
    }

    for (const m of row) {
      downstream.push({
        mappingId: m.id,
        pageIndex: flowPage,
        // Offset within the row is preserved exactly.
        yPct: top + (m.y_pct - rowTop),
      });
    }

    flowBottom = top + rowHeight;
  }

  return {
    fragments,
    downstream,
    // Pages beyond the anchor's own page. Downstream rows can push past the
    // last text fragment, so the highest page index of either wins.
    continuationPages: Math.max(fragments[fragments.length - 1]?.pageIndex ?? 0, flowPage),
  };
}

/**
 * V1 restriction: ONE flow anchor per template page.
 *
 * Two anchors on one page would each claim the remaining space below
 * themselves, and their pushed content would collide in a way no deterministic
 * rule resolves without inventing priority semantics. Rather than allow
 * ambiguous overlap, the FIRST anchor by position wins and the rest are
 * reported so the UI can warn.
 */
export function selectFlowAnchor<T extends FlowMapping>(
  anchors: readonly T[],
): { anchor: T | null; ignored: T[] } {
  if (anchors.length === 0) return { anchor: null, ignored: [] };
  const sorted = anchors.slice().sort((a, b) => a.y_pct - b.y_pct || a.id.localeCompare(b.id));
  return { anchor: sorted[0], ignored: sorted.slice(1) };
}
